/**
 * /settings/tax-codes — Settings → Accounting → Tax codes (G_ACC3).
 *
 * Full CRUD editor: list (filtered by include-archived toggle), Add Tax
 * Code link → /settings/tax-codes/new, inline Edit dialog, Archive
 * confirm dialog.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): upgraded from
 * restore-defaults-only stub to full CRUD surface (G_ACC3 config-admin).
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Archive, Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useArchiveTaxCode, useTaxCodes } from "@/hooks/use-accounting";
import {
  TAX_CODE_KIND_LABELS,
  bpsToPercentString,
  type TaxCode,
} from "@/types/accounting";
import { TaxCodeForm } from "@/components/accounting/tax-code-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTenantSlug } from "@/lib/use-tenant-slug";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/tax-codes",
)({
  component: SettingsTaxCodesRoute,
});

// ---------------------------------------------------------------------------
// Archive confirm dialog
// ---------------------------------------------------------------------------

function ArchiveTaxCodeDialog({
  taxCode,
}: Readonly<{ taxCode: TaxCode }>) {
  const [open, setOpen] = useState(false);
  const mut = useArchiveTaxCode();

  async function onConfirm() {
    try {
      await mut.mutateAsync(taxCode.id);
      toast.success(`${taxCode.code} archived.`);
      setOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-muted-foreground hover:text-destructive"
        >
          <Archive aria-hidden className="h-3.5 w-3.5" />
          Archive
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Archive &ldquo;{taxCode.code}&rdquo;?</DialogTitle>
          <DialogDescription>
            The code is hidden from new invoices and bills. Existing
            journal lines referencing it stay intact (Companies Act
            §128(5)). Tick &ldquo;Include archived&rdquo; on the list to
            bring it back.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={mut.isPending}
            className="gap-1.5"
          >
            {mut.isPending && (
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            )}
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

function EditTaxCodeDialog({
  taxCode,
}: Readonly<{ taxCode: TaxCode }>) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5">
          <Pencil aria-hidden className="h-3.5 w-3.5" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit tax code</DialogTitle>
          <DialogDescription>
            Update the name, rate or GST shape. Code and type are fixed.
          </DialogDescription>
        </DialogHeader>
        <TaxCodeForm
          taxCode={taxCode}
          onSuccess={() => setOpen(false)}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main route
// ---------------------------------------------------------------------------

function SettingsTaxCodesRoute() {
  const tenantSlug = useTenantSlug();
  const { isHrAdmin } = useHrRoles();
  const [includeArchived, setIncludeArchived] = useState(false);

  const q = useTaxCodes({
    include_inactive: includeArchived || undefined,
  });

  const codes = q.data?.items ?? [];

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          {/* Header row */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Tax codes</h2>
              <p className="text-sm text-muted-foreground">
                GST and TDS codes used by the Accounting module.
              </p>
            </div>
            {isHrAdmin && (
              <div className="flex flex-wrap gap-2">
                <RestoreDefaultsButton
                  catalog="tax-codes"
                  catalogLabel="tax codes"
                  invalidateKeys={[["accounting", "tax-codes"]]}
                />
                <Button asChild className="gap-1.5">
                  <Link
                    to="/app/$tenantSlug/settings/tax-codes/new"
                    params={{ tenantSlug }}
                  >
                    <Plus aria-hidden className="h-4 w-4" />
                    Add tax code
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can manage tax codes.
              </AlertDescription>
            </Alert>
          )}

          {/* Include archived toggle */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-archived-tax-codes"
              checked={includeArchived}
              onCheckedChange={(v) => setIncludeArchived(v === true)}
            />
            <Label
              htmlFor="include-archived-tax-codes"
              className="cursor-pointer text-sm text-muted-foreground"
            >
              Include archived
            </Label>
          </div>

          {/* Loading skeletons */}
          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {/* Error */}
          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load tax codes</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {/* Empty state */}
          {q.data && codes.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No tax codes yet.
                {isHrAdmin &&
                  ' Use "Restore defaults" to seed the standard set, or add one manually.'}
              </CardContent>
            </Card>
          )}

          {/* List */}
          {q.data && codes.length > 0 && (
            <div className="space-y-3">
              {codes.map((tc) => (
                <Card
                  key={tc.id}
                  className={tc.is_active ? undefined : "opacity-70"}
                >
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{tc.name}</CardTitle>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <StatusBadge
                            tone="neutral"
                            label={tc.code}
                          />
                          <StatusBadge
                            tone="info"
                            label={TAX_CODE_KIND_LABELS[tc.kind]}
                          />
                          <StatusBadge
                            tone="success"
                            label={`${bpsToPercentString(tc.rate_bps)}%`}
                          />
                          {!tc.is_active && (
                            <StatusBadge tone="neutral" label="Archived" />
                          )}
                          {tc.tds_section && (
                            <StatusBadge
                              tone="neutral"
                              label={`§${tc.tds_section}`}
                            />
                          )}
                          {tc.igst_bps != null && (
                            <StatusBadge tone="neutral" label="Inter-state" />
                          )}
                          {tc.cgst_bps != null && tc.sgst_bps != null && (
                            <StatusBadge tone="neutral" label="Intra-state" />
                          )}
                        </div>
                      </div>
                      {isHrAdmin && tc.is_active && (
                        <div className="flex shrink-0 items-center gap-1">
                          <EditTaxCodeDialog taxCode={tc} />
                          <ArchiveTaxCodeDialog taxCode={tc} />
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
